<template>
  <div class="ka-table">
    <div class="ka-table__header">
      <div>
        <h3 class="ka-table__title">Kiosk Performance</h3>
        <p class="ka-table__subtitle">
          {{ rows.length }} of {{ kiosks.length }} kiosks · click a column to sort
        </p>
      </div>

      <div class="ka-table__filters">
        <div class="ka-table__search">
          <feather-icon icon="SearchIcon" svgClasses="ka-table__search-icon" />
          <input
            v-model.trim="query"
            type="search"
            class="ka-table__search-input"
            placeholder="Search kiosks"
          />
        </div>
        <div
          v-if="hasStatus"
          class="ka-table__segmented"
          role="group"
          aria-label="Status filter"
        >
          <button
            v-for="opt in statusOptions"
            :key="opt.value"
            type="button"
            class="ka-table__seg-btn"
            :class="{ 'is-active': status === opt.value }"
            @click="status = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="ka-table__wrap">
      <table class="ka-table__table">
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col.key"
              class="ka-table__th"
              :class="{ 'ka-table__th--left': col.left, 'is-sorted': sortKey === col.key }"
              @click="sortBy(col.key)"
            >
              {{ col.label }}
              <feather-icon
                v-if="sortKey === col.key"
                :icon="sortDir === 'desc' ? 'ChevronDownIcon' : 'ChevronUpIcon'"
                svgClasses="ka-table__sort-icon"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="k in rows" :key="k.id" class="ka-table__row">
            <td class="ka-table__td ka-table__td--left">
              <div class="ka-table__kiosk">{{ k.name }}</div>
              <div class="ka-table__device" :class="{ 'is-missing': !k.deviceId }">
                {{ k.deviceId || "No device ID" }}
              </div>
            </td>
            <td class="ka-table__td ka-table__td--left">{{ k.venue }}</td>
            <td class="ka-table__td ka-table__td--left">
              <span
                v-if="k.isActive !== null"
                class="ka-table__status"
                :class="k.isActive ? 'is-online' : 'is-offline'"
              >
                <span class="ka-table__status-dot" />
                {{ k.isActive ? "Active" : "Inactive" }}
              </span>
              <div
                :class="k.isActive !== null ? 'ka-table__last' : 'ka-table__seen'"
                :title="k.lastActiveAt || ''"
              >
                {{ lastActive(k.lastActiveAt) }}
              </div>
            </td>
            <td class="ka-table__td">{{ fmt(k.sessions) }}</td>
            <td class="ka-table__td">{{ fmt(k.selfServiceBookings) }}</td>
            <td class="ka-table__td">{{ fmt(k.videoCalls) }}</td>
            <td class="ka-table__td">{{ fmt(k.completed) }}</td>
            <td v-if="showCancelled" class="ka-table__td">{{ fmt(k.cancelled) }}</td>
            <td v-if="showNoFare" class="ka-table__td">{{ fmt(k.noFare) }}</td>
            <template v-if="showPayments">
              <td class="ka-table__td ka-table__td--pay">
                <div>{{ fmt(k.cashBookings) }}</div>
                <div class="ka-table__pay-rev">{{ money(k.cashRevenue) }}</div>
              </td>
              <td class="ka-table__td ka-table__td--pay">
                <div>{{ fmt(k.cardBookings) }}</div>
                <div class="ka-table__pay-rev">{{ money(k.cardRevenue) }}</div>
              </td>
            </template>
            <td class="ka-table__td">
              <div class="ka-table__conv">
                <span class="ka-table__conv-bar">
                  <span :style="{ width: Math.min(100, k.conversion) + '%' }" />
                </span>
                {{ k.conversion.toFixed(1) }}%
              </div>
            </td>
            <td class="ka-table__td ka-table__td--strong">{{ money(k.revenue) }}</td>
          </tr>
          <tr v-if="!rows.length">
            <td class="ka-table__empty" :colspan="visibleColumns.length">No kiosks match these filters</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { formatCurrency, formatNumber } from "../kioskAnalyticsHelpers";

const COLUMNS = [
  { key: "name", label: "Kiosk", left: true },
  { key: "venue", label: "Venue", left: true },
  { key: "lastActiveAt", label: "Last Seen", left: true },
  { key: "sessions", label: "Sessions" },
  { key: "selfServiceBookings", label: "Self-Service" },
  { key: "videoCalls", label: "Video Calls" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled", flag: "showCancelled" },
  { key: "noFare", label: "No Fare", flag: "showNoFare" },
  { key: "cashBookings", label: "Cash", payment: true },
  { key: "cardBookings", label: "Card", payment: true },
  { key: "conversion", label: "Conversion" },
  { key: "revenue", label: "Revenue" },
];

export default {
  name: "KioskPerformanceTable",
  props: {
    kiosks: { type: Array, required: true },
    showPayments: { type: Boolean, default: false },
    showCancelled: { type: Boolean, default: false },
    showNoFare: { type: Boolean, default: false },
  },
  data() {
    return {
      columns: COLUMNS,
      statusOptions: [
        { value: "all", label: "All" },
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ],
      query: "",
      status: "all",
      sortKey: "sessions",
      sortDir: "desc",
    };
  },
  computed: {
    visibleColumns() {
      return this.columns.filter(
        (c) => (!c.payment || this.showPayments) && (!c.flag || this[c.flag])
      );
    },
    // Show the Active/Inactive filter only if the API sends a status per kiosk.
    hasStatus() {
      return this.kiosks.some((k) => k.isActive !== null);
    },
    rows() {
      const q = this.query.toLowerCase();
      const list = this.kiosks.filter((k) => {
        if (this.status === "active" && k.isActive !== true) return false;
        if (this.status === "inactive" && k.isActive !== false) return false;
        if (!q) return true;
        return `${k.name} ${k.venue} ${k.deviceId} ${k.company}`.toLowerCase().includes(q);
      });
      const dir = this.sortDir === "asc" ? 1 : -1;
      const key = this.sortKey;
      return list.slice().sort((a, b) => {
        let av = a[key];
        let bv = b[key];
        if (key === "lastActiveAt") {
          // never-seen kiosks always sort last
          av = av ? new Date(av).getTime() : null;
          bv = bv ? new Date(bv).getTime() : null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
        }
        if (typeof av === "string" || typeof bv === "string") {
          return String(av || "").localeCompare(String(bv || "")) * dir;
        }
        return (Number(av) - Number(bv)) * dir;
      });
    },
  },
  methods: {
    fmt: formatNumber,
    money: formatCurrency,
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === "desc" ? "asc" : "desc";
      } else {
        this.sortKey = key;
        this.sortDir = key === "name" || key === "venue" ? "asc" : "desc";
      }
    },
    lastActive(iso) {
      if (!iso) return "Never seen";
      const then = new Date(iso).getTime();
      if (Number.isNaN(then)) return "—";
      const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
      if (mins < 1) return "Just now";
      if (mins < 60) return `${mins} min ago`;
      const hours = Math.round(mins / 60);
      if (hours < 48) return `${hours} h ago`;
      const days = Math.round(hours / 24);
      if (days <= 30) return `${days} days ago`;
      return new Date(then).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>

<style scoped lang="scss">
.ka-table {
  background: #fff;
  border-radius: 18px;
  padding: 1.5rem;
  border: 1px solid #ececf2;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.25rem;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.35rem;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
  }

  &__search {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__search-icon {
    position: absolute;
    left: 0.75rem;
    width: 15px;
    height: 15px;
    color: #b9b9c3;
    pointer-events: none;
  }

  &__search-input {
    min-height: 40px;
    width: 220px;
    max-width: 100%;
    padding: 0 0.9rem 0 2.1rem;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    font-size: 0.875rem;
    color: #5e5873;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }
  }

  &__segmented {
    display: inline-flex;
    padding: 3px;
    background: #f4f4f7;
    border-radius: 10px;
  }

  &__seg-btn {
    border: none;
    background: transparent;
    padding: 0 0.85rem;
    min-height: 34px;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;

    &.is-active {
      background: #fff;
      color: #7367f0;
      box-shadow: 0 1px 3px rgba(20, 21, 34, 0.08);
    }
  }

  &__wrap {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1080px;
  }

  &__th {
    text-align: right;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #a8a8b3;
    padding: 0.65rem 0.75rem;
    border-bottom: 1px solid #f0f0f4;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &--left {
      text-align: left;
    }

    &.is-sorted {
      color: #7367f0;
    }
  }

  &__sort-icon {
    width: 12px;
    height: 12px;
    vertical-align: middle;
  }

  &__row:hover {
    background: #fafafc;
  }

  &__td {
    text-align: right;
    padding: 0.8rem 0.75rem;
    font-size: 0.875rem;
    color: #5e5873;
    border-bottom: 1px solid #f5f5f8;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &--left {
      text-align: left;
    }

    &--strong {
      font-weight: 700;
      color: #16172b;
    }
  }

  &__td--pay {
    line-height: 1.25;
  }

  &__pay-rev {
    font-size: 0.72rem;
    color: #a8a8b3;
    margin-top: 0.1rem;
  }

  &__kiosk {
    font-weight: 600;
    color: #2d2d2d;
  }

  &__device {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.72rem;
    color: #a8a8b3;
    margin-top: 0.15rem;

    &.is-missing {
      font-family: inherit;
      font-style: italic;
    }
  }

  &__seen {
    font-size: 0.8125rem;
    color: #5e5873;
  }

  &__venue,
  &__last {
    font-size: 0.75rem;
    color: #a8a8b3;
    margin-top: 0.15rem;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    font-weight: 600;

    &.is-online {
      color: #16a34a;
    }

    &.is-offline {
      color: #9ca3af;
    }
  }

  &__status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }

  &__conv {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__conv-bar {
    display: inline-block;
    width: 56px;
    height: 6px;
    border-radius: 999px;
    background: #eef0f4;
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background: #16a34a;
      border-radius: 999px;
    }
  }

  &__empty {
    text-align: center;
    padding: 2rem 1rem;
    color: #a8a8b3;
    font-size: 0.875rem;
  }
}
</style>
